import React, { useEffect, useState } from 'react';
import { Search, Plus, Filter, Download, Users, Eye, Edit2, Trash2, RefreshCw, Loader2, XCircle, Phone, Mail, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { ContactModal } from '../components/ContactModal';
import { contactsApi, CrmContact } from '../services/contactsApi';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { toast } from 'sonner';
export function Contacts() {
  const {
    currentWorkspace,
    currentSubRegion
  } = useWorkspace();
  const [contacts, setContacts] = useState<CrmContact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSearch, setActiveSearch] = useState(''); // committed search term for API
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreRecords, setHasMoreRecords] = useState(false);
  // Modal states
  const [showContactModal, setShowContactModal] = useState(false);
  const [editingContact, setEditingContact] = useState<CrmContact | null>(null);
  const [viewingContact, setViewingContact] = useState<CrmContact | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const perPage = 50;
  const goToPage = async (page: number, search?: string) => {
    const keyword = search ?? activeSearch;
    setLoading(true);
    setError(null);
    try {
      const filters = {
        team: currentWorkspace.name || undefined,
        subRegion: currentSubRegion || undefined
      };
      const hasFilters = !!filters.team || !!filters.subRegion || !!keyword.trim();
      const response = hasFilters ? await contactsApi.search(keyword.trim(), page, perPage, filters) : await contactsApi.list(page, perPage);
      setContacts(response.data || []);
      setCurrentPage(page);
      setHasMoreRecords(response.info?.more_records ?? false);
    } catch (err) {
      console.error('Failed to fetch contacts:', err);
      const msg = err instanceof Error ? err.message : 'Failed to load contacts.';
      setError(msg);
      toast.error('Failed to load contacts', {
        description: msg
      });
    } finally {
      setLoading(false);
    }
  };
  const fetchContacts = () => {
    setActiveSearch('');
    setSearchQuery('');
    goToPage(1, '');
  };
  const handleSearch = () => {
    const keyword = searchQuery.trim();
    setActiveSearch(keyword);
    goToPage(1, keyword);
  };
  const handleClearSearch = () => {
    setSearchQuery('');
    setActiveSearch('');
    goToPage(1, '');
  };
  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };
  useEffect(() => {
    goToPage(1, activeSearch);
  }, [currentWorkspace.name, currentSubRegion]);
  const handleAddContact = () => {
    setEditingContact(null);
    setShowContactModal(true);
  };
  const handleEditContact = (contact: CrmContact) => {
    setEditingContact(contact);
    setShowContactModal(true);
  };
  const handleViewContact = async (contact: CrmContact) => {
    setViewingContact(contact);
    try {
      const fullDetails = await contactsApi.get(contact.id);
      if (fullDetails) {
        setViewingContact(fullDetails);
      }
    } catch (err) {
      console.error('Failed to fetch contact details:', err);
      toast.warning('Could not load full details', {
        description: 'Showing partial data.'
      });
    }
  };
  const handleDeleteContact = async (id: string) => {
    setIsDeleting(true);
    try {
      await contactsApi.delete(id);
      setContacts(prev => prev.filter(c => c.id !== id));
      setShowDeleteConfirm(null);
      toast.success('Contact deleted', {
        description: 'The contact has been removed from Zoho CRM.'
      });
    } catch (err) {
      console.error('Failed to delete contact:', err);
      const msg = err instanceof Error ? err.message : 'Unknown error';
      toast.error('Failed to delete contact', {
        description: msg
      });
    } finally {
      setIsDeleting(false);
    }
  };
  const handleModalSuccess = () => {
    setShowContactModal(false);
    fetchContacts();
  };
  // Team and region filtering is now server-side via search criteria
  const filteredContacts = contacts;
  // Pagination bar component — shown above and below the table
  const PaginationBar = () => {
    if (!hasMoreRecords && currentPage === 1) return null;
    return <div className="flex items-center justify-between py-3 px-4 bg-gray-50 border-t border-gray-200 first:border-t-0 first:border-b" data-id="element-3514">
        <button onClick={() => goToPage(currentPage - 1)} disabled={currentPage <= 1 || loading} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors" data-id="element-3515">
          <ChevronLeft className="h-4 w-4" data-id="element-3516" />
          Previous
        </button>
        <span className="text-sm text-gray-500" data-id="element-3517">
          Page {currentPage}
          {loading && <Loader2 className="inline-block ml-2 h-3.5 w-3.5 animate-spin text-blue-500" data-id="element-3518" />}
        </span>
        <button onClick={() => goToPage(currentPage + 1)} disabled={!hasMoreRecords || loading} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors" data-id="element-3519">
          Next
          <ChevronRight className="h-4 w-4" data-id="element-3520" />
        </button>
      </div>;
  };
  return <div className="space-y-6" data-id="element-3521">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-3522">
        <div className="min-w-0" data-id="element-3523">
          <h1 className="text-2xl font-semibold text-gray-900" data-id="element-3524">Contacts</h1>
          <p className="mt-1 text-sm text-gray-500" data-id="element-3525">
            Manage your CRM contacts directly from here.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0" data-id="element-3526">
          <button onClick={fetchContacts} className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50 whitespace-nowrap" title="Refresh List" data-id="element-3527">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} data-id="element-3528" />
          </button>
          <button className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50 whitespace-nowrap" data-id="element-3529">
            <Download className="h-4 w-4 flex-shrink-0" data-id="element-3530" />
            <span data-id="element-3531">Export</span>
          </button>
          <button onClick={handleAddContact} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 whitespace-nowrap" data-id="element-3532">
            <Plus className="h-4 w-4 flex-shrink-0" data-id="element-3533" />
            <span data-id="element-3534">Add Contact</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-4 bg-white border border-gray-200 rounded-lg p-4" data-id="element-3535">
        <div className="relative flex-1 min-w-0 flex gap-2" data-id="element-3536">
          <div className="relative flex-1" data-id="element-3537">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" data-id="element-3538" />
            <input type="text" placeholder="Search by name or email..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} onKeyDown={handleSearchKeyDown} className="pl-9 pr-8 py-2 w-full border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500" data-id="element-3539" />
            {searchQuery && <button onClick={handleClearSearch} className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-600 rounded" title="Clear search" data-id="element-3540">
                <X className="h-3.5 w-3.5" data-id="element-3541" />
              </button>}
          </div>
          <button onClick={handleSearch} disabled={loading} className="inline-flex items-center gap-1.5 px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 flex-shrink-0" title="Search" data-id="element-3542">
            <Search className="h-4 w-4" data-id="element-3543" />
          </button>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0" data-id="element-3544">
          <Filter className="h-4 w-4 text-gray-400 hidden sm:block flex-shrink-0" data-id="element-3545" />
          <span className="text-sm text-gray-500" data-id="element-3546">
            {contacts.length} records
            {activeSearch && <span className="ml-1 text-blue-600" data-id="element-3547">for "{activeSearch}"</span>}
          </span>
        </div>
      </div>

      {/* Error State */}
      {error && <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm" data-id="element-3548">
          {error}
        </div>}

      {/* Contacts Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-3549">
        {loading && contacts.length === 0 ? <div className="p-12 text-center" data-id="element-3550">
            <Loader2 className="mx-auto h-8 w-8 text-blue-500 animate-spin" data-id="element-3551" />
            <p className="mt-2 text-sm text-gray-500" data-id="element-3552">Loading contacts...</p>
          </div> : filteredContacts.length === 0 ? <div className="p-12 text-center" data-id="element-3553">
            <Users className="mx-auto h-12 w-12 text-gray-400" data-id="element-3554" />
            <h3 className="mt-2 text-sm font-semibold text-gray-900" data-id="element-3555">
              No contacts found
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-3556">
              {searchQuery ? 'Try adjusting your search.' : 'Add your first contact to get started.'}
            </p>
            {!searchQuery && <button onClick={handleAddContact} className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700" data-id="element-3557">
                <Plus className="h-4 w-4" data-id="element-3558" />
                Add First Contact
              </button>}
          </div> : <>
            {/* Pagination — Top */}
            <PaginationBar data-id="element-3559" />

            <div className="overflow-x-auto" data-id="element-3560">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-3561">
                <thead className="bg-gray-50" data-id="element-3562">
                  <tr data-id="element-3563">
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-3564">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-3565">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-3566">
                      Phone
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-3567">
                      Tags
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-3568">
                      Mailing Lists
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-3569">
                      Categories
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-3570">
                      Created Time
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-3571">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-3572">
                  {filteredContacts.map(contact => <tr key={contact.id} className="hover:bg-gray-50 group" data-id="element-3573">
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-3574">
                        <div className="flex items-center gap-3" data-id="element-3575">
                          <div className="h-9 w-9 rounded-full bg-gray-100 flex items-center justify-center text-sm font-medium text-gray-600 flex-shrink-0" data-id="element-3576">
                            {(contact.First_Name?.[0] || contact.Last_Name?.[0] || '?').toUpperCase()}
                          </div>
                          <div className="min-w-0" data-id="element-3577">
                            <div className="text-sm font-medium text-gray-900 truncate max-w-[200px]" data-id="element-3578">
                              {contact.Full_Name || `${contact.First_Name || ''} ${contact.Last_Name || ''}`}
                            </div>
                            {contact.Title && <div className="text-xs text-gray-500 truncate" data-id="element-3579">
                                {contact.Title}
                              </div>}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-3580">
                        {contact.Email ? <div className="flex items-center gap-2 text-sm text-gray-600" data-id="element-3581">
                            <Mail className="h-3 w-3 text-gray-400" data-id="element-3582" />
                            {contact.Email}
                          </div> : <span className="text-xs text-gray-400" data-id="element-3583">—</span>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-3584">
                        {contact.Phone || contact.Mobile ? <div className="flex items-center gap-2 text-sm text-gray-600" data-id="element-3585">
                            <Phone className="h-3 w-3 text-gray-400" data-id="element-3586" />
                            {contact.Phone || contact.Mobile}
                          </div> : <span className="text-xs text-gray-400" data-id="element-3587">—</span>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-3588">
                        {contact.Tags ? <div className="flex flex-wrap gap-1 max-w-[200px]" data-id="element-3589">
                            {contact.Tags.split(',').slice(0, 2).map((tag, i) => <span key={i} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800" data-id="element-3590">
                                  {tag.trim()}
                                </span>)}
                            {contact.Tags.split(',').length > 2 && <span className="text-xs text-gray-500" data-id="element-3591">
                                +{contact.Tags.split(',').length - 2}
                              </span>}
                          </div> : <span className="text-xs text-gray-400" data-id="element-3592">—</span>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-3593">
                        {contact.Mailing_List ? <div className="flex flex-wrap gap-1 max-w-[200px]" data-id="element-3594">
                            {contact.Mailing_List.split(',').slice(0, 2).map((list, i) => <span key={i} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700" data-id="element-3595">
                                  {list.trim()}
                                </span>)}
                            {contact.Mailing_List.split(',').length > 2 && <span className="text-xs text-gray-500" data-id="element-3596">
                                +{contact.Mailing_List.split(',').length - 2}
                              </span>}
                          </div> : <span className="text-xs text-gray-400" data-id="element-3597">—</span>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-3598">
                        {contact.Categories ? <div className="flex flex-wrap gap-1 max-w-[200px]" data-id="element-3599">
                            {contact.Categories.split(',').slice(0, 2).map((cat, i) => <span key={i} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-50 text-purple-700" data-id="element-3600">
                                  {cat.trim()}
                                </span>)}
                            {contact.Categories.split(',').length > 2 && <span className="text-xs text-gray-500" data-id="element-3601">
                                +{contact.Categories.split(',').length - 2}
                              </span>}
                          </div> : <span className="text-xs text-gray-400" data-id="element-3602">—</span>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-3603">
                        {contact.Created_Time ? new Date(contact.Created_Time).toLocaleDateString() : '—'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right" data-id="element-3604">
                        <div className="flex items-center justify-end gap-1" data-id="element-3605">
                          <button onClick={() => handleViewContact(contact)} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded flex-shrink-0" title="View Details" data-id="element-3606">
                            <Eye className="h-4 w-4" data-id="element-3607" />
                          </button>
                          <button onClick={() => handleEditContact(contact)} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded flex-shrink-0" title="Edit Contact" data-id="element-3608">
                            <Edit2 className="h-4 w-4" data-id="element-3609" />
                          </button>
                          <button onClick={() => setShowDeleteConfirm(contact.id)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded flex-shrink-0" title="Delete Contact" data-id="element-3610">
                            <Trash2 className="h-4 w-4" data-id="element-3611" />
                          </button>
                        </div>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>

            {/* Pagination — Bottom */}
            <PaginationBar data-id="element-3612" />
          </>}
      </div>

      {/* Contact Modal (Add/Edit) */}
      <ContactModal isOpen={showContactModal} onClose={() => {
      setShowContactModal(false);
      setEditingContact(null);
    }} contact={editingContact} onSuccess={handleModalSuccess} data-id="element-3613" />

      {/* View Contact Detail Modal */}
      {viewingContact && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-3614">
          <div className="flex min-h-screen items-center justify-center p-4" data-id="element-3615">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setViewingContact(null)} data-id="element-3616" />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-lg" data-id="element-3617">
              <div className="px-6 py-4 border-b border-gray-200" data-id="element-3618">
                <div className="flex items-center justify-between" data-id="element-3619">
                  <h3 className="text-lg font-semibold text-gray-900" data-id="element-3620">
                    Contact Details
                  </h3>
                  <button onClick={() => setViewingContact(null)} className="text-gray-400 hover:text-gray-500" data-id="element-3621">
                    <XCircle className="h-5 w-5" data-id="element-3622" />
                  </button>
                </div>
              </div>
              <div className="px-6 py-4 space-y-4" data-id="element-3623">
                <div className="flex items-center gap-4" data-id="element-3624">
                  <div className="h-14 w-14 rounded-full bg-gray-100 flex items-center justify-center text-lg font-medium text-gray-600" data-id="element-3625">
                    {(viewingContact.First_Name?.[0] || viewingContact.Last_Name?.[0] || '?').toUpperCase()}
                  </div>
                  <div data-id="element-3626">
                    <h4 className="text-lg font-medium text-gray-900" data-id="element-3627">
                      {viewingContact.Full_Name || `${viewingContact.First_Name || ''} ${viewingContact.Last_Name || ''}`}
                    </h4>
                    <p className="text-sm text-gray-500" data-id="element-3628">
                      {viewingContact.Title}{' '}
                      {viewingContact.Title && viewingContact.Department ? '•' : ''}{' '}
                      {viewingContact.Department}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-100" data-id="element-3629">
                  <div data-id="element-3630">
                    <p className="text-xs font-medium text-gray-500 uppercase" data-id="element-3631">
                      Email
                    </p>
                    <p className="mt-1 text-sm text-gray-900 break-all" data-id="element-3632">
                      {viewingContact.Email || '—'}
                    </p>
                  </div>
                  <div data-id="element-3633">
                    <p className="text-xs font-medium text-gray-500 uppercase" data-id="element-3634">
                      Phone
                    </p>
                    <p className="mt-1 text-sm text-gray-900" data-id="element-3635">
                      {viewingContact.Phone || '—'}
                    </p>
                  </div>
                  <div data-id="element-3636">
                    <p className="text-xs font-medium text-gray-500 uppercase" data-id="element-3637">
                      Mobile
                    </p>
                    <p className="mt-1 text-sm text-gray-900" data-id="element-3638">
                      {viewingContact.Mobile || '—'}
                    </p>
                  </div>
                  <div data-id="element-3639">
                    <p className="text-xs font-medium text-gray-500 uppercase" data-id="element-3640">
                      Owner
                    </p>
                    <p className="mt-1 text-sm text-gray-900" data-id="element-3641">
                      {viewingContact.Owner?.name || '—'}
                    </p>
                  </div>
                  <div data-id="element-3642">
                    <p className="text-xs font-medium text-gray-500 uppercase" data-id="element-3643">
                      Tags
                    </p>
                    <p className="mt-1 text-sm text-gray-900" data-id="element-3644">
                      {viewingContact.Tags || '—'}
                    </p>
                  </div>
                  <div data-id="element-3645">
                    <p className="text-xs font-medium text-gray-500 uppercase" data-id="element-3646">
                      Mailing Lists
                    </p>
                    <p className="mt-1 text-sm text-gray-900" data-id="element-3647">
                      {viewingContact.Mailing_List || '—'}
                    </p>
                  </div>
                  <div className="col-span-2" data-id="element-3648">
                    <p className="text-xs font-medium text-gray-500 uppercase" data-id="element-3649">
                      Categories
                    </p>
                    <p className="mt-1 text-sm text-gray-900" data-id="element-3650">
                      {viewingContact.Categories || '—'}
                    </p>
                  </div>
                </div>

                <div className="pt-4 border-t border-gray-100 text-xs text-gray-400" data-id="element-3651">
                  <p data-id="element-3652">
                    Created:{' '}
                    {viewingContact.Created_Time ? new Date(viewingContact.Created_Time).toLocaleString() : '—'}
                  </p>
                  <p data-id="element-3653">
                    Modified:{' '}
                    {viewingContact.Modified_Time ? new Date(viewingContact.Modified_Time).toLocaleString() : '—'}
                  </p>
                  <p className="mt-1 font-mono" data-id="element-3654">ID: {viewingContact.id}</p>
                </div>
              </div>
              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-3" data-id="element-3655">
                <button onClick={() => setViewingContact(null)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-3656">
                  Close
                </button>
                <button onClick={() => {
              handleEditContact(viewingContact);
              setViewingContact(null);
            }} className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700" data-id="element-3657">
                  Edit Contact
                </button>
              </div>
            </div>
          </div>
        </div>}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-3658">
          <div className="flex min-h-screen items-center justify-center p-4" data-id="element-3659">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setShowDeleteConfirm(null)} data-id="element-3660" />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md p-6" data-id="element-3661">
              <div className="flex items-center gap-4" data-id="element-3662">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-100 flex items-center justify-center" data-id="element-3663">
                  <Trash2 className="h-6 w-6 text-red-600" data-id="element-3664" />
                </div>
                <div data-id="element-3665">
                  <h3 className="text-lg font-medium text-gray-900" data-id="element-3666">
                    Delete Contact
                  </h3>
                  <p className="mt-1 text-sm text-gray-500" data-id="element-3667">
                    Are you sure you want to delete this contact from Zoho CRM?
                    This action cannot be undone.
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6" data-id="element-3668">
                <button onClick={() => setShowDeleteConfirm(null)} disabled={isDeleting} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-3669">
                  Cancel
                </button>
                <button onClick={() => handleDeleteContact(showDeleteConfirm)} disabled={isDeleting} className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center gap-2" data-id="element-3670">
                  {isDeleting && <Loader2 className="h-4 w-4 animate-spin" data-id="element-3671" />}
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}